import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Sparkles,
  Plus,
  MessageSquare,
  TrendingUp,
  Trash2,
  ArrowRight,
  Loader2,
  Zap,
  Globe,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { formatEUR, countryFlag } from "../lib/format";
import OpportunityBadge from "../components/OpportunityBadge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import TrattativaModal from "../components/TrattativaModal";

const ACTIVE_SESSION_KEY = "autexio_active_session";

export default function AICopilotPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [insights, setInsights] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(
    () => localStorage.getItem(ACTIVE_SESSION_KEY) || null
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => searchParams.get("prompt") || "");
  const [loading, setLoading] = useState(false);
  const [rehydrating, setRehydrating] = useState(true);
  const [trattativaVehicle, setTrattativaVehicle] = useState(null);
  const scrollRef = useRef(null);
  const focusVehicleId = searchParams.get("vehicle");

  const suggested = useMemo(() => t("ai.suggestions", { returnObjects: true }) || [], [t]);

  // Auto-scroll bottom on new messages / loading state.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const loadInsights = useCallback(() => {
    api.get("/ai/insights").then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(data || []);
      return data || [];
    } catch {
      return [];
    }
  }, []);

  const openSession = useCallback(async (sid) => {
    if (!sid) {
      setActiveSessionId(null);
      setMessages([]);
      localStorage.removeItem(ACTIVE_SESSION_KEY);
      return;
    }
    try {
      const { data } = await api.get(`/ai/sessions/${sid}`);
      setActiveSessionId(data.id);
      setMessages(data.messages || []);
      localStorage.setItem(ACTIVE_SESSION_KEY, data.id);
    } catch (e) {
      // Session was deleted or belongs to another user — drop the stale pointer.
      localStorage.removeItem(ACTIVE_SESSION_KEY);
      setActiveSessionId(null);
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    loadInsights();
    (async () => {
      const list = await loadSessions();
      // Rehydration priority: (1) persisted active session that still exists,
      // (2) latest session for continuity — unless the user is entering the
      // page with a focus-vehicle context (from "Ask AI about this car").
      const persisted = localStorage.getItem(ACTIVE_SESSION_KEY);
      const listIds = new Set((list || []).map((s) => s.id));
      let target = null;
      if (persisted && listIds.has(persisted)) target = persisted;
      else if (!focusVehicleId && list && list.length > 0) target = list[0].id;
      if (target) {
        await openSession(target);
      } else {
        localStorage.removeItem(ACTIVE_SESSION_KEY);
      }
      setRehydrating(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Note: input is initialized synchronously from ?prompt= (see useState above)
  // so a hard navigation from vehicle detail via "Ask AI" pre-fills the field
  // even before any effects run.

  const sendMessage = useCallback(
    async (text) => {
      if (!text || !text.trim() || loading) return;
      const now = new Date().toISOString();
      const userMsg = { role: "user", content: text.trim(), ts: now, vehicles: [] };
      setMessages((m) => [...m, userMsg]);
      setInput("");
      setLoading(true);
      try {
        const { data } = await api.post("/ai/chat", {
          message: text.trim(),
          language: i18n.language,
          session_id: activeSessionId,
          vehicle_id: focusVehicleId || undefined,
        });
        const assistantMsg = {
          role: "assistant",
          content: data.text,
          vehicles: data.vehicles || [],
          ts: new Date().toISOString(),
        };
        setMessages((m) => [...m, assistantMsg]);
        setActiveSessionId(data.session_id);
        if (data.session_id) {
          localStorage.setItem(ACTIVE_SESSION_KEY, data.session_id);
        }
        loadSessions();
      } catch (e) {
        toast.error(formatApiError(e));
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: t("ai.error"),
            vehicles: [],
            ts: new Date().toISOString(),
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [activeSessionId, i18n.language, focusVehicleId, loading, loadSessions, t]
  );

  const newChat = () => openSession(null);

  const deleteSession = async (sid, ev) => {
    ev.stopPropagation();
    try {
      await api.delete(`/ai/sessions/${sid}`);
      if (sid === activeSessionId) newChat();
      loadSessions();
    } catch (e) {
      // swallow — non-critical UX cleanup
    }
  };

  return (
    <div className="py-6 md:py-10" data-testid="ai-copilot">
      {/* Header */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {t("ai.overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
          {t("ai.title")}
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("ai.subtitle")}</p>
      </div>

      {/* Insights */}
      {insights && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div
            className="lg:col-span-2 rounded-2xl bg-white border border-slate-100 p-5 shadow-sm"
            data-testid="ai-insights-opportunities"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-600" />
                {t("ai.opportunities")}
              </h2>
              <Link
                to="/marketplace?sort=ai_score"
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
              >
                {t("home.view_all")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <ul className="space-y-2">
              {insights.opportunities.map((v, i) => (
                <li key={v.id}>
                  <Link
                    to={`/vehicle/${v.id}`}
                    className="group flex items-center gap-3 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/40 transition-colors p-3"
                    data-testid={`ai-opp-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-heading font-bold text-sm tabular-nums shrink-0">
                      {Math.round(v.ai_score)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-semibold text-sm tracking-tight truncate">
                        {v.brand} {v.model} {v.trim || ""}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {countryFlag(v.country)} {v.dealer_city || v.country} · {v.year} · {v.km?.toLocaleString("it-IT")} km
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-bold text-sm tabular-nums text-slate-950">
                        {formatEUR(v.price_eur)}
                      </div>
                      <div className="text-xs font-semibold tabular-nums text-emerald-600">
                        +{formatEUR(v.estimated_margin_eur || 0)}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="rounded-2xl bg-[#0A0A0B] text-white p-5 relative overflow-hidden"
            data-testid="ai-insights-pulse"
          >
            <div className="absolute -right-16 -bottom-20 w-52 h-52 rounded-full bg-blue-600/25 blur-3xl" />
            <div className="relative z-10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold inline-flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> {t("ai.market_pulse")}
              </div>
              <h3 className="font-heading font-bold text-lg tracking-tight mt-1">
                {t("ai.pulse_title")}
              </h3>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {["IT", "DE"].map((c) => (
                  <div key={c} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <div className="text-xs text-slate-400">
                      {countryFlag(c)} {c === "IT" ? "Italia" : "Germania"}
                    </div>
                    <div className="mt-1 font-heading font-bold text-base tabular-nums">
                      {formatEUR(insights.countries?.[c]?.avg_margin || 0)}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                      {t("ai.avg_margin_short")}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                  {t("ai.hottest_segments")}
                </div>
                <ul className="mt-2 space-y-1.5">
                  {insights.segments?.slice(0, 4).map((s) => (
                    <li key={s.segment} className="flex items-center justify-between text-xs">
                      <span className="capitalize">{t(`segments.${s.segment}`, s.segment)}</span>
                      <span className="tabular-nums font-semibold text-emerald-400">
                        {formatEUR(s.avg_margin)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat area */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* Sessions list (desktop) */}
        <aside className="hidden lg:block" data-testid="ai-sessions-panel">
          <Button
            onClick={newChat}
            data-testid="new-chat-button"
            className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white h-10 font-semibold text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> {t("ai.new_chat")}
          </Button>
          <ul className="mt-3 space-y-1 max-h-[560px] overflow-y-auto hide-scrollbar">
            {sessions.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => openSession(s.id)}
                  data-testid={`session-${s.id}`}
                  className={`group w-full text-left flex items-start gap-2 rounded-xl px-3 py-2 text-xs transition-colors ${
                    activeSessionId === s.id
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0 truncate font-medium">{s.title}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => deleteSession(s.id, e)}
                    className="opacity-0 group-hover:opacity-100 shrink-0 hover:text-red-500 cursor-pointer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Chat */}
        <div
          className="rounded-2xl bg-white border border-slate-100 shadow-sm flex flex-col min-h-[560px]"
          data-testid="ai-chat-panel"
        >
          {/* Mobile new-chat header */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              {i18n.language.toUpperCase()}
            </div>
            <button
              onClick={newChat}
              data-testid="new-chat-button-mobile"
              className="text-xs font-semibold inline-flex items-center gap-1 text-blue-600"
            >
              <Plus className="w-3.5 h-3.5" /> {t("ai.new_chat")}
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
            data-testid="ai-messages"
          >
            {messages.length === 0 && !loading && !rehydrating && (
              <EmptyState suggestions={suggested} onPick={sendMessage} />
            )}
            {messages.length === 0 && rehydrating && (
              <div
                data-testid="ai-rehydrating"
                className="flex items-center justify-center py-10 text-slate-400"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            )}
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <Bubble key={i} msg={m} onRequest={setTrattativaVehicle} />
              ))}
              {loading && <TypingBubble />}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
            className="border-t border-slate-100 p-3 md:p-4 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("ai.input_placeholder")}
              disabled={loading}
              data-testid="ai-chat-input"
              className="flex-1 h-11 rounded-full border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-500 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="ai-chat-send"
              className="h-11 w-11 shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      </div>

      <TrattativaModal
        open={!!trattativaVehicle}
        onOpenChange={(v) => { if (!v) setTrattativaVehicle(null); }}
        vehicle={trattativaVehicle}
        defaultType="acquisto"
      />
    </div>
  );
}

function EmptyState({ suggestions, onPick }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center text-center py-6 md:py-10">
      <div className="w-14 h-14 rounded-2xl bg-[#0A0A0B] flex items-center justify-center text-white shadow-lg">
        <Sparkles className="w-6 h-6 text-blue-400" />
      </div>
      <h3 className="mt-4 font-heading font-bold text-xl tracking-tight text-slate-950">
        {t("ai.empty_title")}
      </h3>
      <p className="mt-1 text-sm text-slate-500 max-w-lg">{t("ai.empty_subtitle")}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-2xl">
        {(Array.isArray(suggestions) ? suggestions : []).map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            data-testid={`ai-suggestion-${i}`}
            className="text-xs font-medium bg-slate-50 hover:bg-blue-50 hover:text-blue-700 text-slate-700 border border-slate-200 rounded-full px-3 py-1.5 transition-colors"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({ msg, onRequest }) {
  const { t } = useTranslation();
  const isUser = msg.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[86%] md:max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? "bg-blue-600 text-white rounded-br-sm"
            : "bg-slate-50 border border-slate-100 text-slate-800 rounded-bl-sm"
        }`}
        data-testid={isUser ? "user-message" : "assistant-message"}
      >
        <MessageText content={msg.content} isUser={isUser} />
        {!isUser && msg.vehicles?.length > 0 && (
          <div className="mt-3 space-y-2">
            {msg.vehicles.map((v) => (
              <ChatVehicleCard key={v.id} v={v} onRequest={onRequest} />
            ))}
          </div>
        )}
        {!isUser && (
          <p
            data-testid="ai-message-disclaimer"
            className="mt-3 text-[10px] italic text-slate-400 leading-snug border-t border-slate-200/60 pt-2"
          >
            {t("ai.disclaimer")}
          </p>
        )}
      </div>
    </motion.div>
  );
}

function MessageText({ content, isUser }) {
  // Basic markdown-lite: **bold**, line breaks
  const parts = String(content || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <div className={`whitespace-pre-wrap ${isUser ? "" : "text-slate-800"}`}>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className={isUser ? "text-white" : "text-slate-950"}>
            {p.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </div>
  );
}

function ChatVehicleCard({ v, onRequest }) {
  const { t } = useTranslation();
  const a = v.analysis || {};
  return (
    <div
      data-testid={`chat-vehicle-${v.id}`}
      className="bg-white rounded-xl border border-slate-200 hover:border-blue-400 transition-colors overflow-hidden"
    >
      <Link
        to={`/vehicle/${v.id}`}
        className="flex items-center gap-3 p-2.5"
      >
        {v.images?.[0] && (
          <img
            src={v.images[0]}
            alt=""
            className="w-16 h-12 rounded-lg object-cover shrink-0"
            loading="lazy"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold text-xs tracking-tight text-slate-950 truncate">
            {v.brand} {v.model}
          </div>
          <div className="text-[11px] text-slate-500 truncate mt-0.5">
            {v.year} · {v.km?.toLocaleString("it-IT")} km · {v.fuel} · {countryFlag(v.country)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-bold text-xs tabular-nums text-slate-950">
            {formatEUR(v.price_eur)}
          </div>
          <div className="text-[11px] font-semibold tabular-nums text-emerald-600">
            +{formatEUR(a.estimated_margin_eur || 0)}
          </div>
        </div>
        <OpportunityBadge index={a.opportunity_index} size="sm" />
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRequest?.(v);
        }}
        data-testid={`chat-vehicle-request-${v.id}`}
        className="w-full text-left px-3 py-2 border-t border-slate-100 bg-slate-50 hover:bg-blue-50 text-[11px] font-semibold text-blue-700 inline-flex items-center justify-between transition-colors"
      >
        <span className="inline-flex items-center gap-1.5">
          <Send className="w-3 h-3" /> {t("ai.send_request")}
        </span>
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

function TypingBubble() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex justify-start"
      data-testid="typing-indicator"
    >
      <div className="bg-slate-50 border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </motion.div>
  );
}
